import { UserDto } from '../../../models/user/dto/user.dto';

const formatters = new Map<string, Intl.NumberFormat>();

/**
 * Formats an amount in cents as a localized currency string, in the currency
 * of the PaymentIntent that charged it (EUR when the caller does not know it —
 * a USD tenant used to read "15,00 €" for a dollar charge).
 * `null`/`undefined` returns an em dash so the cell still has visual weight.
 */
export function formatCoveredAmount(
    amountCents?: number | null,
    currency?: string,
): string {
    if (amountCents === null || amountCents === undefined) {
        return '—';
    }
    const code = (currency || 'EUR').toUpperCase();
    let formatter = formatters.get(code);
    if (!formatter) {
        try {
            formatter = new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: code,
            });
        } catch {
            // Not an ISO 4217 code: show the number with the raw code.
            return `${(amountCents / 100).toFixed(2)} ${code}`;
        }
        formatters.set(code, formatter);
    }
    return formatter.format(amountCents / 100);
}

/**
 * Renders the "Paid by" cell label for a covered slot:
 * "Alice D. · 15,00 €". Uses the payer's first name + last-initial.
 * Returns null when the slot isn't covered (caller renders an em dash).
 */
export function formatPaidByLabel(
    payer: UserDto | undefined,
    coveredAmountCents: number | undefined | null,
    currency?: string,
): string | null {
    if (!payer) return null;
    const lastInitial = payer.lastName ? `${payer.lastName.charAt(0)}.` : '';
    const namePart = lastInitial
        ? `${payer.firstName} ${lastInitial}`
        : payer.firstName;
    const amountPart = formatCoveredAmount(coveredAmountCents, currency);
    return `${namePart} · ${amountPart}`;
}

/**
 * Returns true when the row represents a pay-for-others slot — i.e. the
 * participant has a `paidBy` set AND the payer is a different user from the
 * participant. Self-pay rows (paidBy missing OR paidBy === participant) are
 * not covered slots.
 */
export function isCoveredSlot(
    participantId: string | undefined,
    payer: UserDto | undefined,
): boolean {
    if (!payer) return false;
    if (!participantId) return false;
    return payer.id !== participantId;
}
